import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { 
  Clock, 
  Calendar, 
  Crown,
  Star,
  User,
  AtSign,
  Shield
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import UserActionsDropdown from './UserActionsDropdown';
import { getProfileBorderStyle } from '@/utils/profileBorderUtils';
import { useImageUrl } from '@/hooks/useImageUrl';
import { getRoleStyle, shouldHighlightRole } from '@/utils/roleStyleUtils';

interface UserCardProps {
  user: any;
  currentUserIsAdmin: boolean;
  onViewProfile: (username: string) => void;
  onUserAction: (action: string, userId: string) => void;
  onResetAccount: (user: any) => void;
  onManageRole?: (user: any) => void;
}

const UserCard: React.FC<UserCardProps> = ({ 
  user,
  currentUserIsAdmin,
  onViewProfile, 
  onUserAction, 
  onResetAccount,
  onManageRole 
}) => {
  const formatLastLogin = (lastLogin: string | null) => {
    if (!lastLogin) return 'Nunca logou';
    
    try {
      return formatDistanceToNow(new Date(lastLogin), { 
        addSuffix: true, 
        locale: ptBR 
      });
    } catch {
      return 'Data inválida';
    }
  };

  const formatCreatedAt = (createdAt: string) => {
    try {
      return formatDistanceToNow(new Date(createdAt), { 
        addSuffix: true, 
        locale: ptBR 
      });
    } catch {
      return 'Data inválida';
    }
  };

  const getPlanTypeDisplay = (planType: string) => {
    switch (planType) {
      case 'premium':
        return {
          label: 'PREMIUM',
          icon: Crown,
          className: 'bg-yellow-500/20 text-yellow-400'
        };
      case 'standard':
        return {
          label: 'STANDARD',
          icon: Star,
          className: 'bg-blue-500/20 text-blue-400'
        };
      case 'free':
      default:
        return null;
    }
  };

  const planDisplay = getPlanTypeDisplay(user.plan_type);
  
  const isPremium = user.plan_type && !['free', 'sponsor_free', 'sponsored_free'].includes(user.plan_type);
  const isVerified = user.verification_level === 'full';
  const borderStyle = getProfileBorderStyle(isPremium, isVerified, user.is_online || false);

  // Use async hook for CDN-cached avatar URL
  const { url: userAvatarUrl } = useImageUrl(user.main_photo_url, 'avatar');

  // Get role styling
  const userRole = user.user_role || 'user';
  const roleStyle = getRoleStyle(userRole);
  const isPrivilegedUser = shouldHighlightRole(userRole);

  return (
    <Card className={`bg-white/5 ${isPrivilegedUser ? roleStyle.border : borderStyle.containerClass} ${isPrivilegedUser ? roleStyle.glow : borderStyle.animation || ''}`}>
      <CardContent className="p-4">
        {/* Role Badge for Admin/Moderator */}
        {isPrivilegedUser && (
          <div className={`mb-3 px-3 py-2 rounded-lg ${roleStyle.badge} flex items-center justify-center gap-2 text-sm`}>
            <span className="text-xl">{roleStyle.icon}</span>
            <span className="font-bold tracking-wide">{roleStyle.label}</span>
          </div>
        )}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="relative">
              <Avatar className="h-12 w-12">
                <AvatarImage src={userAvatarUrl} />
                <AvatarFallback className="bg-white/10 text-white text-lg font-bold">
                  {user.username.substring(0, 2).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              {user.is_online && (
                <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 border-2 border-black rounded-full"></div>
              )}
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="font-medium text-white">{user.username}</h3>
              {user.city && user.state_code && (
                <p className="text-sm text-white/70">
                  {user.city}, {user.state_code}
                </p>
              )}
              
              {/* Email, Last Login and Created At */}
              <div className="flex flex-col gap-1 mt-1">
                {user.email && (
                  <div className="flex items-center gap-1 text-xs text-white/70">
                    <AtSign className="h-3 w-3" />
                    <span>{user.email}</span>
                  </div>
                )}
                <div className="flex items-center gap-1 text-xs text-white/70">
                  <Clock className="h-3 w-3" />
                  <span>Último login: {formatLastLogin(user.last_login)}</span>
                </div>
                <div className="flex items-center gap-1 text-xs text-white/70">
                  <Calendar className="h-3 w-3" />
                  <span>Criado: {formatCreatedAt(user.created_at)}</span>
                </div>
              </div>
              
              <div className="flex items-center gap-2 mt-2">
                {user.verification_level === 'full' && (
                  <span className="text-xs bg-green-500/20 text-white px-2 py-0.5 rounded">
                    Verificado
                  </span>
                )}
                {planDisplay && (
                  <span className={`text-xs px-2 py-0.5 rounded flex items-center gap-1 ${planDisplay.className.replace(/text-\w+-\d+/, 'text-white')}`}>
                    <planDisplay.icon className="h-3 w-3" />
                    {planDisplay.label}
                  </span>
                )}
                {user.hidden && (
                  <span className="text-xs bg-orange-500/20 text-white px-2 py-0.5 rounded">
                    Oculto
                  </span>
                )}
                {!user.onboarding_completed && (
                  <span className="text-xs bg-yellow-500/20 text-white px-2 py-0.5 rounded">
                    Incompleto
                  </span>
                )}
              </div>
            </div>
          </div>

        <UserActionsDropdown
          user={user}
          currentUserIsAdmin={currentUserIsAdmin}
          onViewProfile={onViewProfile}
          onUserAction={onUserAction}
          onResetAccount={onResetAccount}
          onManageRole={onManageRole}
        />
        </div>
      </CardContent>
    </Card>
  );
};

export default UserCard;
